import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Users, Download, MessageSquarePlus, ClipboardCheck, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type Metric = { label: string; value: string | number; icon: typeof Users; hint?: string };

export function BetaInsightsPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["beta-insights"],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [events, feedback] = await Promise.all([
        supabase
          .from("analytics_events")
          .select("id, event_type, metadata, created_at")
          .gte("created_at", since)
          .limit(5000),
        supabase
          .from("beta_feedback")
          .select("id, category, message, page_path, device, status, created_at")
          .order("created_at", { ascending: false })
          .limit(50),
      ]);

      return {
        events: events.data ?? [],
        feedback: feedback.data ?? [],
      };
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const events = data?.events ?? [];
  const feedback = data?.feedback ?? [];

  const dau = new Set(
    events
      .filter(
        (e) =>
          new Date(e.created_at as string).getTime() >
          Date.now() - 24 * 60 * 60 * 1000,
      )
      .map((e) => (e.metadata as any)?.session_id)
      .filter(Boolean),
  ).size;

  const installs = events.filter((e) => e.event_type === "pwa_install_accepted").length;
  const quizCompletions = events.filter((e) => e.event_type === "quiz_completed").length;
  const feedbackCount = feedback.length;
  const betaSignups = events.filter((e) => e.event_type === "beta_signup").length;

  const sessionDurations = events
    .filter((e) => e.event_type === "session_ping" && (e.metadata as any)?.duration_ms)
    .map((e) => Number((e.metadata as any).duration_ms));
  const avgMin = sessionDurations.length
    ? Math.round(
        sessionDurations.reduce((a, b) => a + b, 0) / sessionDurations.length / 60000,
      )
    : 0;

  const metrics: Metric[] = [
    { label: "Daily active (24h)", value: dau, icon: Users },
    { label: "Beta signups (7d)", value: betaSignups, icon: Users },
    { label: "Avg session", value: avgMin ? `${avgMin} min` : "—", icon: Clock },
    { label: "PWA installs (7d)", value: installs, icon: Download },
    { label: "Quiz completions (7d)", value: quizCompletions, icon: ClipboardCheck },
    { label: "Feedback (recent)", value: feedbackCount, icon: MessageSquarePlus },
  ];

  const featureUsage = new Map<string, number>();
  for (const e of events) {
    featureUsage.set(e.event_type, (featureUsage.get(e.event_type) ?? 0) + 1);
  }
  const topFeatures = Array.from(featureUsage.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {metrics.map((m) => (
          <div key={m.label} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <m.icon className="h-4 w-4" />
              <span className="text-xs">{m.label}</span>
            </div>
            <div className="mt-1 text-2xl font-bold text-foreground">{m.value}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-semibold text-foreground mb-3">Feature usage (7d)</h3>
          {topFeatures.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No events tracked yet. Instrumented events will appear here.
            </p>
          ) : (
            <ul className="space-y-2 text-sm">
              {topFeatures.map(([name, count]) => (
                <li key={name} className="flex justify-between">
                  <span className="text-muted-foreground">{name}</span>
                  <span className="font-semibold text-foreground">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-semibold text-foreground mb-3">Recent beta feedback</h3>
          {feedback.length === 0 ? (
            <p className="text-sm text-muted-foreground">No feedback yet.</p>
          ) : (
            <ul className="space-y-3 max-h-96 overflow-y-auto">
              {feedback.slice(0, 15).map((f) => (
                <li key={f.id} className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline">{f.category}</Badge>
                    <span className="text-xs text-muted-foreground">{f.page_path}</span>
                    <span className="text-xs text-muted-foreground">· {f.device}</span>
                  </div>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{f.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Notes: Resume uploads, job applications, and profile-completion metrics aren't yet
        instrumented because those features don't exist in this project.
      </p>
    </div>
  );
}